import { useCallback } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { TranslationProvider, useTranslation } from '@nycelogic/nycebuddy-chat-ui/i18n';
import { ThemeRoot } from '@nycelogic/nycebuddy-chat-ui';
import { Login } from './pages/Login';
import { Chat } from './pages/Chat';
import { Launch } from './pages/Launch';
import { PdfExport } from './pages/PdfExport';
import { Authorize } from './pages/Authorize';
import { client, prefs } from './auth/session';
import { useLogin } from './auth/useSession';
import { useUpdateAvailable } from './version';

const RESERVED = new Set(['admin', 'pdf-export', 'api', 'launch', 'mcp', 'authorize']);

function UpdateBanner() {
  const { t } = useTranslation();
  const available = useUpdateAvailable();
  if (!available) return null;
  return (
    <ThemeRoot theme="dark" style={{ background: 'transparent' }}>
      <div className="nb-update-banner">
        <span>{t('common.newVersion')}</span>
        <button type="button" onClick={() => window.location.reload()}>
          {t('common.reload')}
        </button>
      </div>
    </ThemeRoot>
  );
}

function Home({ fixedSystemId }: { fixedSystemId?: string }) {
  const login = useLogin();
  const rerender = useCallback(() => {}, []);
  if (login) return <Chat login={login} onLoggedOut={rerender} />;
  return (
    <div className="app">
      <Login fixedSystemId={fixedSystemId} onLogin={rerender} />
    </div>
  );
}

function SystemRoute() {
  const { systemId = '' } = useParams();
  if (RESERVED.has(systemId.toLowerCase())) {
    window.location.href = `/${systemId}/`;
    return null;
  }
  return <Home fixedSystemId={systemId} />;
}

export default function App() {
  const loader = useCallback((lang: string) => client.translations(lang), []);
  const login = useLogin();
  const language = login?.language ?? 'en';
  document.body.dataset.theme = prefs.theme() === 'auto' ? '' : prefs.theme();
  return (
    <TranslationProvider language={language} loader={loader}>
      <BrowserRouter>
        <UpdateBanner />
        <Routes>
          <Route path="/launch" element={<Launch />} />
          <Route path="/pdf-export" element={<PdfExport />} />
          <Route path="/authorize" element={<Authorize />} />
          <Route path="/:systemId" element={<SystemRoute />} />
          <Route path="/" element={<Home />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </TranslationProvider>
  );
}
